import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Edit, Plus, Trash2, Search } from "lucide-react";
import { getMenus, getMenusByRole, addRoleMenu, getRoles } from "helpers/backend_helper";
import BreadCrumb from "Common/BreadCrumb";
import TableContainer from "Common/TableContainer";
import Modal from "Common/Components/Modal";
import DeleteModal from "Common/DeleteModal";
import { ToastContainer } from "react-toastify";
import { Common } from "helpers/common";
import BulkRoleMenuForm from "./BulkRoleMenuForm";


const RoleMenuList = () => {
    const [roleMenus, setRoleMenus] = useState<any[]>([]);
    const [menus, setMenus] = useState<any[]>([]);
    const [roles, setRoles] = useState<any[]>([]);
    const [selectedRole, setSelectedRole] = useState<number>(2); // Default role ID
    const [loading, setLoading] = useState<boolean>(true);
    const [show, setShow] = useState<boolean>(false);
    const [roleMenuData, setRoleMenuData] = useState<any>(null);
    const [isEdit, setIsEdit] = useState<boolean>(false);
    const [deleteModal, setDeleteModal] = useState<boolean>(false);
    const [eventData, setEventData] = useState<any>();
    const uiHelper = new Common();

    useEffect(() => {
        fetchRoleMenus();
    }, [selectedRole]);



    useEffect(() => {
        fetchData();
    }, []);

    const fetchRoleMenus = async () => {
        try {
            const response = await getMenusByRole(selectedRole);
            setRoleMenus(response.data || []);
        } catch (error) {
            console.error("Error fetching role menus:", error);
            setRoleMenus([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchData = async () => {
        try {
            const [menusResponse, roleMenusResponse, rolesResponse] = await Promise.all([
                getMenus(),
                getMenusByRole(selectedRole),
                getRoles()
            ]);
            setMenus(menusResponse.data || []);
            setRoleMenus(roleMenusResponse.data || []);
            setRoles(rolesResponse.data || []);
        } catch (error) {

            console.error("Error fetching data:", error);
        } finally {
            setLoading(false);
        }
    };

    // Delete Modal
    const deleteToggle = () => setDeleteModal(!deleteModal);

    // Delete Data
    const onClickDelete = (cell: any) => {
        setDeleteModal(true);
        if (cell.id) {
            setEventData(cell);
        }
    };

    const handleDelete = async () => {
        if (eventData) {
            // TODO: Implement delete role menu API call
            setDeleteModal(false);
            uiHelper.showSuccessMessage('Role menu mapping has been deleted successfully.')
            setRoleMenus(roleMenus => roleMenus.filter((item: any) => item.id !== eventData.id));
        }
    };

    // Update Data
    const handleEditDataClick = (ele: any) => {
        setRoleMenuData({ ...ele });
        setIsEdit(true);
        setShow(true);
    };

    const handleAddRoleMenu = () => {
        setRoleMenuData({ roleId: selectedRole });
        setIsEdit(false);
        toggle();
    }

    const onSave = (results: any[]) => {
        setRoleMenus((prevRoleMenus) => [...results, ...prevRoleMenus]);
        toggle();
    }

    const toggle = useCallback(() => {
        setShow(!show);
        if (show) {
            setRoleMenuData(null);
        }
    }, [show]);

    // columns
    const Status = ({ item }: any) => {
        switch (item) {
            case true:
                return (<span className="status px-2.5 py-0.5 inline-block text-xs font-medium rounded border bg-green-100 border-transparent text-green-500 dark:bg-green-500/20 dark:border-transparent">Active</span>);
            case false:
                return (<span className="status px-2.5 py-0.5 inline-block text-xs font-medium rounded border bg-orange-100 border-transparent text-orange-500 dark:bg-orange-500/20 dark:border-transparent">InActive</span>);
        }
    };

    const columns = [
        {
            header: "Menu Name",
            accessorKey: "menuId",
            enableColumnFilter: true,
            enableSorting: true,
            cell: (cell: any) => {
                const menu = menus.find(m => m.id === cell.getValue());
                return menu ? menu.name : 'N/A';
            }
        },
        {
            header: "Status",
            accessorKey: "isActive",
            enableColumnFilter: false,
            enableSorting: false,
            cell: (cell: any) => (
                <Status item={cell.getValue()} />
            ),
        },
        {
            header: "Action",
            enableColumnFilter: false,
            enableSorting: false,
            cell: (cell: any) => (
                <div className="flex justify-start gap-2">
                    <Link to="#!"
                        className="flex items-center justify-center size-8 transition-all duration-200 ease-linear rounded-md text-slate-500 bg-slate-100 hover:text-white hover:bg-slate-500 dark:text-zink-200 dark:bg-zink-600 dark:hover:text-white dark:hover:bg-zink-400"
                        onClick={() => {
                            const data = cell.row.original;
                            handleEditDataClick(data);
                        }}>
                        <Edit className="inline-block size-4 ltr:mr-1 rtl:ml-1" />
                    </Link>
                    <Link to="#!"
                        className="flex items-center justify-center size-8 transition-all duration-200 ease-linear rounded-md text-red-500 bg-red-100 hover:text-white hover:bg-red-500 dark:bg-red-500/20 dark:hover:bg-red-500"
                        onClick={() => {
                            const menuData = cell.row.original;
                            onClickDelete(menuData);
                        }}>
                        <Trash2 className="inline-block size-4 ltr:mr-1 rtl:ml-1" />
                    </Link>
                </div>
            ),
        }
    ];

    return (
        <React.Fragment>
            <DeleteModal show={deleteModal} onHide={deleteToggle} onDelete={handleDelete} />
            <ToastContainer closeButton={false} limit={1} />
            
            <div className="grid grid-cols-1 gap-x-5 xl:grid-cols-12" style={{ marginTop: 15 }}>
                <div className="xl:col-span-12">
                    <div className="card" id="roleMenuTable">
                        <div className="flex items-center border-y border-dashed" style={{ padding: 10 }}>
                            <div className="flex items-center gap-4 grow">
                                <div className="flex items-center gap-2">
                                    <label htmlFor="roleSelect" className="text-15 font-medium">Select Role:</label>
                                    <select
                                        id="roleSelect"
                                        className="form-select border-slate-200 w-[150px] dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800"
                                        value={selectedRole}
                                        onChange={(e) => setSelectedRole(Number(e.target.value))}
                                    >
                                        {roles.map((role) => (
                                            <option key={role.id} value={role.id}>
                                                {role.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div className="shrink-0">
                                <button type="button" className="text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20" onClick={handleAddRoleMenu}>
                                    <Plus className="inline-block size-4" /> <span className="align-middle">Add Role Menu</span>
                                </button>
                            </div>
                        </div>
                        {loading ? (
                            <div className="flex items-center justify-center h-32">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-custom-500"></div>
                            </div>
                        ) : (
                            <div>
                                {roleMenus && roleMenus?.length > 0 ? (
                                    <TableContainer
                                        isPagination={true}
                                        columns={columns}
                                        data={roleMenus}
                                        customPageSize={10}
                                        divclassName="overflow-x-auto"
                                        tableclassName="w-full whitespace-nowrap"
                                        theadclassName="ltr:text-left rtl:text-right bg-slate-100 dark:bg-zink-600"
                                        thclassName="px-3.5 py-1.5 font-semibold border-b border-slate-200 dark:border-zink-500"
                                        tdclassName="px-3.5 py-1.0 border-y border-slate-200 dark:border-zink-500"
                                        PaginationClassName="flex flex-col items-center gap-4 px-4 mt-3 md:flex-row"
                                        SearchPlaceholder='Search...'
                                        isGlobalFilter={false}
                                        isSelect={true}
                                    />
                                ) : (
                                    <div className="noresult">
                                        <div className="py-6 text-center">
                                            <Search className="size-6 mx-auto text-sky-500 fill-sky-100 dark:sky-500/20" />
                                            <h5 className="mt-2 mb-1">No Menu Mappings Found</h5>
                                            <p className="text-slate-500 dark:text-zink-200">No menu mappings have been assigned to this role yet.</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Role Menu Form Modal */}
            <Modal show={show} onHide={toggle} id="defaultModal" modal-center="true"
                className="fixed flex flex-col transition-all duration-300 ease-in-out left-2/4 z-drawer -translate-x-2/4 -translate-y-2/4"
                dialogClassName="w-screen md:w-[30rem] bg-white shadow rounded-md dark:bg-zink-600">
                {roleMenuData ? <BulkRoleMenuForm onSave={onSave} onHide={toggle} data={roleMenuData} menus={menus} roles={roles} /> : <></>}
            </Modal>
        </React.Fragment>
    );
};

export default RoleMenuList; 